import * as React from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue } from 'jotai';
import tw, { styled } from 'twin.macro';
import { useQueryClient } from '@tanstack/react-query';

import { RestworldBigLogo } from '@restworld/utils-common';
import { ButtonV2, IconButton, ProgressBar, Title } from '@restworld/ui-ds';
import { useAuth } from '@restworld/feature-authentication';

import RestaurantOnboarding from '../../restaurants/restaurant-onboarding-modal/restaurant-onboarding';
import {
  restaurantOnboardingFormDefaultValues,
  RestaurantOnboardingStage,
  activeRestaurantOnboardingStateAtom,
} from '../../restaurants/restaurant-onboarding-modal';
import { RestaurantOnboardingPayload } from '../../restaurants/restaurant-onboarding-modal/restaurant-onboarding-form';
import { LayoutWrapper } from '../../../layouts';
import { useV2Router } from '../../dashboard/hooks/use-v2-router';
import { useRestaurantInformationOnboardingQueryKey } from '../../restaurants/restaurant-onboarding-modal/use-restaurant-onboarding-data-query';
import { googleAddresInputValueAtom } from '../../restaurants/restaurant-onboarding-modal/google-address-input-result';
import { RestaurantOnboardingFooter } from '../../restaurants/restaurant-onboarding-modal/restaurant-onboarding-footer';
import {
  useRestaurantOnboardingFormReset,
  useRestaurantOnboardingResetActions,
} from '../../restaurants/restaurant-onboarding-modal/restaurant-onboarding-modal.hooks';

const stateTransitions: Partial<Record<RestaurantOnboardingStage, RestaurantOnboardingStage>> = {
  restaurant_onboarding_form: 'select_restaurant_from_google',
  magic_ai_loading: 'select_restaurant_from_google',
  restaurant_already_exists: 'select_restaurant_from_google',
  restaurant_owner_onboarding_form: 'restaurant_onboarding_form',
  contact_already_exists: 'restaurant_owner_onboarding_form',
  otp_verification: 'restaurant_owner_onboarding_form',
};
const RestaurantOnboardingWrapperStyled = styled.div<{
  activeState?: RestaurantOnboardingStage;
}>`
  ${tw`px-4 md:px-0 mt-[67px] mb-[80px] h-[calc(100vh - 148px)] relative overflow-auto`}
  ${({ activeState }) =>
    (activeState === 'restaurant_onboarding_form' || activeState === 'restaurant_owner_onboarding_form') &&
    tw`mt-[94px] mb-[80px] h-[calc(100vh - 175px)]`}
  ${({ activeState }) => activeState === 'restaurant_onboarding_successfull' && tw`px-0 my-0 h-screen`}
`;

const HeaderActions = ({ onBackButton, onCloseAction }: { onBackButton: () => void; onCloseAction: () => void }) => {
  const { t: tC } = useTranslation('common');
  const [activeState] = useAtom(activeRestaurantOnboardingStateAtom);

  return (
    <div tw="w-full px-4 py-3 self-stretch border-b border-neutral-40 inline-flex flex-col justify-start items-center gap-3 fixed top-0 bg-neutral-10 z-10">
      <div tw="w-full md:(max-w-[38rem] min-w-[38rem]) flex flex-row items-center justify-between">
        <div>
          {activeState !== 'select_restaurant_from_google' && (
            <ButtonV2 variant="tertiary" StartIcon={ChevronLeftIcon} tw="shadow-none" onClick={onBackButton}>
              <Title content={tC('back')} />
            </ButtonV2>
          )}
          {activeState === 'select_restaurant_from_google' && <RestworldBigLogo />}
        </div>
        <IconButton
          Icon={XMarkIcon}
          ariaLabel="close-icon"
          onClick={onCloseAction}
          disabled={activeState === 'select_restaurant_from_google'}
        />
      </div>
      {(activeState === 'restaurant_onboarding_form' || activeState === 'restaurant_owner_onboarding_form') && (
        <ProgressBar
          percent={activeState === 'restaurant_onboarding_form' ? 70 : 90}
          customIndicatorStyles={tw`max-w-[38rem] w-full rounded-full h-2 bg-neutral-40`}
          customStyles={tw`bg-green-500 rounded-full h-2`}
        />
      )}
    </div>
  );
};

const FooterActions = ({
  activeState,
  isInputFilled,
  methods,
}: {
  activeState: RestaurantOnboardingStage;
  isInputFilled: boolean;
  methods: UseFormReturn<RestaurantOnboardingPayload, any>;
}) => {
  const router = useV2Router();
  const { t } = useTranslation('restaurant-onboarding');
  const isGettingStartedPage = router.pathname === '/getting-started';
  const shouldRenderFooter =
    ![
      'contact_already_exists',
      'restaurant_already_exists',
      'otp_verification',
      'restaurant_onboarding_successfull',
      'magic_ai_loading',
    ].includes(activeState) && !(activeState === 'select_restaurant_from_google' && !isInputFilled);

  if (activeState === 'select_restaurant_from_google' && !isInputFilled && isGettingStartedPage)
    return (
      <div tw="w-full flex justify-center left-0 bottom-[40px] absolute text-center">
        <h5 tw="text-neutral-200 text-sm font-medium">
          {t('copyright_restworld', {
            year: new Date().getFullYear(),
          })}
        </h5>
      </div>
    );
  if (!shouldRenderFooter) return null;

  return (
    <div tw="w-full min-h-[80px] py-3 border-t border-neutral-40 bg-neutral-10 self-end inline-flex flex-row justify-center items-center gap-3 fixed bottom-0">
      <div tw="w-full max-w-[40rem] px-4 flex flex-col justify-start items-start gap-3">
        <RestaurantOnboardingFooter methods={methods} />
      </div>
    </div>
  );
};

const RestaurantOrganizationOnboardingHeader = () => {
  const queryClient = useQueryClient();
  const [activeState, setActiveState] = useAtom(activeRestaurantOnboardingStateAtom);
  const restaurantInfoQueryKey = useRestaurantInformationOnboardingQueryKey();
  const { resetState } = useRestaurantOnboardingResetActions();

  const cancelInformationAPIQueries = React.useCallback(
    () =>
      queryClient.cancelQueries({
        queryKey: restaurantInfoQueryKey,
      }),
    [queryClient, restaurantInfoQueryKey]
  );

  const onBackButton = React.useCallback(() => {
    cancelInformationAPIQueries();
    const previousState = stateTransitions[activeState];
    if (previousState) setActiveState(previousState);
  }, [activeState, cancelInformationAPIQueries, setActiveState]);

  const onCloseAction = React.useCallback(() => {
    cancelInformationAPIQueries();
    resetState();
  }, [cancelInformationAPIQueries, resetState]);

  return <HeaderActions onBackButton={onBackButton} onCloseAction={onCloseAction} />;
};

const EmployerGettingStartedPage: React.FunctionComponent = () => {
  const { isAuthenticated, user } = useAuth();

  const methods = useForm<RestaurantOnboardingPayload>({
    defaultValues: {
      ...restaurantOnboardingFormDefaultValues,
      ...{
        email: user?.email || '',
        firstName: user?.name || '',
        lastName: user?.surname || '',
        phone: user?.phone_number || '',
      },
    },
    mode: 'all',
  });
  const activeState = useAtomValue(activeRestaurantOnboardingStateAtom);
  const googleInputAddressValue = useAtomValue(googleAddresInputValueAtom);
  const router = useV2Router();
  const isInputFilled = React.useMemo(() => googleInputAddressValue?.length > 0, [googleInputAddressValue?.length]);

  React.useEffect(() => {
    if (user?.type === 'lead') return;
    if (isAuthenticated && activeState !== 'restaurant_onboarding_successfull') router.push('/employer/homepage');
    if (isAuthenticated && user?.type !== 'lead' && activeState !== 'restaurant_onboarding_successfull')
      router.push('/employer/homepage');
  }, [activeState, isAuthenticated, router, user?.type]);

  useRestaurantOnboardingFormReset({ methods });

  return (
    <LayoutWrapper hideNavigation title="Getting started" hasNavbar={false} hasFooter={false}>
      <div tw="overflow-hidden">
        {activeState !== 'restaurant_onboarding_successfull' && <RestaurantOrganizationOnboardingHeader />}
        <RestaurantOnboardingWrapperStyled activeState={activeState}>
          <RestaurantOnboarding methods={methods} />
        </RestaurantOnboardingWrapperStyled>
        <FooterActions activeState={activeState} isInputFilled={isInputFilled} methods={methods} />
      </div>
    </LayoutWrapper>
  );
};

export default EmployerGettingStartedPage;
